import type { CSSProperties } from "react";
import type { StudentAccessibilityPreferences } from "./studentAccessibilityApi.js";

const HEX = /^#[0-9a-fA-F]{6}$/;
const FALLBACK_BACKGROUND = "#f6f6f4";
const FALLBACK_TEXT = "#1b1b1a";
const BRAND_DARK_INK = "#1b1b1a";
const BRAND_LIGHT_INK = "#ffffff";

export interface StudentAccessibilityPalette {
  id: string;
  label: string;
  backgroundColour: string;
  textColour: string;
}

/**
 * Useful starting points, not medical presets. Every pair clears 4.5:1 for
 * ordinary text; a student can still choose their own pair below.
 */
export const STUDENT_ACCESSIBILITY_PALETTES: readonly StudentAccessibilityPalette[] = [
  { id: "cream", label: "Soft cream", backgroundColour: "#fff8dc", textColour: "#1b1b1a" },
  { id: "blue", label: "Pale blue", backgroundColour: "#eef6ff", textColour: "#13243a" },
  { id: "green", label: "Pale green", backgroundColour: "#eef8ee", textColour: "#16301a" },
  { id: "grey", label: "Soft grey", backgroundColour: "#f0f0f0", textColour: "#202020" },
  { id: "dark", label: "Dark", backgroundColour: "#1c2330", textColour: "#f5f7fa" },
];

function parseHex(value: string): [number, number, number] | null {
  if (!HEX.test(value)) return null;
  return [
    Number.parseInt(value.slice(1, 3), 16),
    Number.parseInt(value.slice(3, 5), 16),
    Number.parseInt(value.slice(5, 7), 16),
  ];
}

function hexPart(value: number): string {
  return Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, "0");
}

function toHex([red, green, blue]: [number, number, number]): string {
  return `#${hexPart(red)}${hexPart(green)}${hexPart(blue)}`;
}

function mix(first: string, second: string, secondWeight: number): string {
  const a = parseHex(first);
  const b = parseHex(second);
  if (!a || !b) return first;
  const weight = Math.max(0, Math.min(1, secondWeight));
  return toHex([
    a[0] * (1 - weight) + b[0] * weight,
    a[1] * (1 - weight) + b[1] * weight,
    a[2] * (1 - weight) + b[2] * weight,
  ]);
}

function linearChannel(value: number): number {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function luminance(value: string): number {
  const rgb = parseHex(value);
  if (!rgb) return 0;
  return (
    0.2126 * linearChannel(rgb[0]) +
    0.7152 * linearChannel(rgb[1]) +
    0.0722 * linearChannel(rgb[2])
  );
}

/** WCAG contrast ratio for two six-digit hex colours. */
export function contrastRatio(first: string, second: string): number {
  const lighter = Math.max(luminance(first), luminance(second));
  const darker = Math.min(luminance(first), luminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}

function rootColour(variable: "--bg" | "--ink", fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  return HEX.test(value) ? value.toLowerCase() : fallback;
}

export function currentStudentDefaultColours(): { backgroundColour: string; textColour: string } {
  return {
    backgroundColour: rootColour("--bg", FALLBACK_BACKGROUND),
    textColour: rootColour("--ink", FALLBACK_TEXT),
  };
}

/** Resolve `default` only when a concrete colour is required for checking/mixing. */
export function effectiveStudentColours(preferences: StudentAccessibilityPreferences): {
  backgroundColour: string;
  textColour: string;
} {
  const defaults = currentStudentDefaultColours();
  return {
    backgroundColour:
      preferences.backgroundColour === "default"
        ? defaults.backgroundColour
        : preferences.backgroundColour.toLowerCase(),
    textColour:
      preferences.textColour === "default"
        ? defaults.textColour
        : preferences.textColour.toLowerCase(),
  };
}

export function hasCustomStudentColours(preferences: StudentAccessibilityPreferences): boolean {
  return preferences.backgroundColour !== "default" || preferences.textColour !== "default";
}

export function studentAccessibilityContrast(preferences: StudentAccessibilityPreferences): number {
  const colours = effectiveStudentColours(preferences);
  return contrastRatio(colours.backgroundColour, colours.textColour);
}

export function studentAccessibilityColoursAreReadable(
  preferences: StudentAccessibilityPreferences,
): boolean {
  return studentAccessibilityContrast(preferences) >= 4.5;
}

/**
 * Keep muted copy genuinely readable instead of reducing opacity. Find the
 * softest blend toward the background which still retains 4.5:1 contrast on
 * every surface muted copy can sit on (page, panel, line and the accent tint),
 * not just the page background.
 */
function readableMuted(text: string, background: string, surfaces: readonly string[]): string {
  const all = [background, ...surfaces];
  const passes = (candidate: string) => all.every((surface) => contrastRatio(candidate, surface) >= 4.5);
  if (!passes(text)) return text;
  let best = text;
  for (let backgroundWeight = 0.05; backgroundWeight <= 0.45; backgroundWeight += 0.05) {
    const candidate = mix(text, background, backgroundWeight);
    if (!passes(candidate)) break;
    best = candidate;
  }
  return best;
}

/**
 * The two WMF logo files differ only in whether the wordmark ink is dark or
 * light. Pick whichever has stronger contrast with the actual Hub background;
 * this is deliberately independent of the operating-system theme.
 */
function applyBrandMarkContrast(
  style: CSSProperties & Record<string, string | number | undefined>,
  backgroundColour: string,
): void {
  const useDarkInk =
    contrastRatio(backgroundColour, BRAND_DARK_INK) >=
    contrastRatio(backgroundColour, BRAND_LIGHT_INK);
  style["--brand-mark-light-display"] = useDarkInk ? "block" : "none";
  style["--brand-mark-dark-display"] = useDarkInk ? "none" : "block";
}

/**
 * Apply only the choices the student actually changed. Default values inherit
 * the ordinary Hub CSS so future visual changes and system dark mode continue
 * to work without rewriting stored preference rows.
 *
 * Colour values get a second fail-safe here even though the editor refuses to
 * save a low-contrast pair. The API contract validates shape, not contrast, so
 * an older client or direct write must never be able to make the whole family
 * shell unreadable. In that case text/font/spacing still apply and colours fall
 * back to the ordinary Hub theme.
 */
export function studentAccessibilityStyle(
  preferences: StudentAccessibilityPreferences,
): CSSProperties {
  const style: CSSProperties & Record<string, string | number | undefined> = {};

  if (preferences.fontSize === "large") style.fontSize = "17px";
  if (preferences.fontSize === "extra-large") style.fontSize = "20px";

  if (preferences.fontFamily === "sans-readable") {
    style.fontFamily = 'Arial, Verdana, "Trebuchet MS", sans-serif';
  }
  if (preferences.fontFamily === "serif-readable") {
    style.fontFamily = 'Georgia, "Times New Roman", serif';
  }

  if (preferences.textSpacing === "increased") {
    style.lineHeight = 1.65;
    style.letterSpacing = "0.06em";
    style.wordSpacing = "0.1em";
  }
  if (preferences.textSpacing === "extra") {
    style.lineHeight = 1.8;
    style.letterSpacing = "0.12em";
    style.wordSpacing = "0.16em";
  }

  if (hasCustomStudentColours(preferences)) {
    const { backgroundColour, textColour } = effectiveStudentColours(preferences);
    if (contrastRatio(backgroundColour, textColour) >= 4.5) {
      style["--bg"] = backgroundColour;
      style["--ink"] = textColour;
      const panel = mix(backgroundColour, textColour, 0.045);
      const line = mix(backgroundColour, textColour, 0.2);
      const accentSoft = mix(backgroundColour, textColour, 0.12);
      style["--panel"] = panel;
      style["--line"] = line;
      style["--muted"] = readableMuted(textColour, backgroundColour, [panel, line, accentSoft]);
      // The stock accent is a fixed brand red for the light/dark themes only.
      // Against an arbitrary chosen palette it can clash or fall below 4.5:1,
      // so emphasis surfaces follow the chosen ink over a faint tint instead.
      style["--accent"] = textColour;
      style["--accent-soft"] = accentSoft;
      applyBrandMarkContrast(style, backgroundColour);
      style.backgroundColor = "var(--bg)";
      style.color = "var(--ink)";
    }
  }

  return style;
}
